import {
    Textarea,
    Checkbox,
    FormLabel,
    VStack,
    Box,
    HStack,
    Input,
    FormControl,
    InputGroup,
    InputLeftElement,
    Text,
    Wrap,
    WrapItem,
    Tag,
    useToast,
    Flex,
    Alert,
    AlertDescription,
    Icon,
} from '@chakra-ui/react';
import { Button, SegmentedControl } from '../ui';
import { useState, useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import { TbAlertTriangle } from 'react-icons/tb';
import { useAddPartOptionNote } from '../hooks/useAddPartOptionNote';
import { useAddEstimatingSystemNote } from '../hooks/useAddEstimatingSystemNote';
import { PartOption } from '../hooks/useGetPartOptions';
import {
    LKQ_NO_ADJUST_CALLOUT,
    canUsePartsTraderNoteDestination,
    getInitialNoteDrafts,
    isLkqPartType,
    isNoteDraftDirty,
    requiresNonMceLkqNote,
    type NoteDestination,
    type NoteDrafts,
} from '../utils/partOptionNotes';

const PREDEFINED_NOTES = [
    "Delivery time too long",
    "Incorrect/invalid part",
    "Part does not meet insurer policy",
    "Part delivered was unfit or damaged",
    "Part not available when ordered",
    "Price not competitive after rental",
    "Price not competitive after cleanup",
    "Already Approved",
    "Price Matched",
    "Customer declined aftermarket parts",
    "One time use part",
];

export interface PartOptionNoteFormHandle {
    save: () => void;
    isPending: boolean;
}

interface PartOptionNoteFormProps {
    selectedOptionId: string;
    orderId: string;
    orderPartId: string;
    notes?: string;
    estimatingNote?: string;
    listPriceAdjusted: boolean;
    isEditable: boolean;
    /** List price can be changed even when notes and selection stay locked. */
    canAdjustListPrice?: boolean;
    onClose: () => void;
    refetch: () => void | Promise<unknown>;
    compact?: boolean;
    hideButtons?: boolean;
    showHeader?: boolean;
    onPendingChange?: (pending: boolean) => void;
    onModifiedListPriceChange?: (price: number | null) => void;
    onSaved?: (note: string) => void;
    partType?: string;
    source?: PartOption['source'];
    insurerBest?: boolean;
    drpJob?: boolean;
}

const PartOptionNoteForm = forwardRef<PartOptionNoteFormHandle, PartOptionNoteFormProps>(({
    selectedOptionId,
    orderId,
    orderPartId,
    notes = '',
    estimatingNote: estimatingNoteProp = '',
    listPriceAdjusted,
    isEditable,
    canAdjustListPrice = isEditable,
    onClose,
    refetch,
    compact = false,
    hideButtons = false,
    showHeader = true,
    onPendingChange,
    onModifiedListPriceChange,
    onSaved,
    partType,
    source,
    insurerBest,
    drpJob = false,
}, ref) => {
    const cannotAdjustListPrice = isLkqPartType(partType);
    const requiresNote = requiresNonMceLkqNote({
        partType,
        drpJob,
        source,
        insurerBest,
    });
    const showPartsTraderDestinations = canUsePartsTraderNoteDestination({
        drpJob,
        source,
    });
    const initialDestination: NoteDestination = requiresNote || showPartsTraderDestinations
        ? 'marketplace'
        : 'estimating';

    const [drafts, setDrafts] = useState<NoteDrafts>(() => getInitialNoteDrafts({
        marketplaceNote: notes,
        estimatingNote: estimatingNoteProp,
        requiresNote,
    }));
    const [removeAdjustment, setRemoveAdjustment] = useState(!cannotAdjustListPrice);
    const [changeAdjustment, setChangeAdjustment] = useState(false);
    const [modifiedListPrice, setModifiedListPrice] = useState('');
    const [isPending, setIsPending] = useState(false);
    const [noteDestination, setNoteDestination] = useState<NoteDestination>(initialDestination);
    const userSelectedDestination = useRef(false);
    const toast = useToast();
    const addNoteMutation = useAddPartOptionNote();
    const addEstimatingNoteMutation = useAddEstimatingSystemNote();

    const activeDraft = drafts[noteDestination];

    const setActiveDraft = (value: string) => {
        setDrafts((prev) => ({ ...prev, [noteDestination]: value }));
    };

    const handleDestinationChange = (key: NoteDestination) => {
        userSelectedDestination.current = true;
        if (key === 'both') {
            setDrafts((prev) => ({
                ...prev,
                both: prev[noteDestination],
            }));
        } else if (noteDestination === 'both') {
            setDrafts((prev) => ({
                ...prev,
                [key]: prev.both,
            }));
        }
        setNoteDestination(key);
    };

    const destinationOptions = useMemo((): Array<{
        key: NoteDestination;
        label: string;
        disabled: boolean;
        disabledTooltip?: string;
    }> => {
        if (showPartsTraderDestinations || requiresNote) {
            return [
                { key: 'marketplace', label: 'PartsTrader', disabled: false },
                {
                    key: 'estimating',
                    label: 'Estimate',
                    disabled: requiresNote,
                    disabledTooltip: requiresNote
                        ? 'A PartsTrader note is required before saving this recycled option.'
                        : undefined,
                },
                { key: 'both', label: 'Both', disabled: false },
            ];
        }
        return [{ key: 'estimating', label: 'Estimate', disabled: false }];
    }, [requiresNote, showPartsTraderDestinations]);

    useEffect(() => {
        if (requiresNote) {
            setNoteDestination((prev) => (prev === 'estimating' ? 'marketplace' : prev));
            if (!userSelectedDestination.current) {
                setNoteDestination('marketplace');
            }
            return;
        }
        if (!showPartsTraderDestinations) {
            setNoteDestination('estimating');
            return;
        }
        if (!userSelectedDestination.current) {
            setNoteDestination('marketplace');
        }
    }, [requiresNote, showPartsTraderDestinations]);

    useEffect(() => {
        if (cannotAdjustListPrice || !changeAdjustment || !modifiedListPrice) {
            onModifiedListPriceChange?.(null);
            return;
        }
        const parsed = parseFloat(modifiedListPrice);
        onModifiedListPriceChange?.(isNaN(parsed) || parsed <= 0 ? null : parsed);
    }, [cannotAdjustListPrice, changeAdjustment, modifiedListPrice, onModifiedListPriceChange]);

    const handlePredefinedNoteClick = (predefinedNote: string) => {
        if (!isEditable) return;
        const newNote = activeDraft.trim() ? `${activeDraft}\n${predefinedNote}` : predefinedNote;
        setActiveDraft(newNote.slice(0, 499));
    };

    const handleSave = async () => {
        if ((!isEditable && !canAdjustListPrice) || !selectedOptionId) return;

        const wantsChangeListPrice = !cannotAdjustListPrice
            && changeAdjustment
            && !!modifiedListPrice
            && modifiedListPrice.trim() !== '';
        const wantsRemoveListPrice = !cannotAdjustListPrice
            && listPriceAdjusted
            && removeAdjustment;
        // Estimate-only destinations still need the marketplace mutation for list-price changes.
        const shouldMutateListPrice = wantsChangeListPrice || wantsRemoveListPrice;

        const marketplaceDirty = isEditable && isNoteDraftDirty(drafts.marketplace, notes);
        const estimatingDirty = isEditable && isNoteDraftDirty(drafts.estimating, estimatingNoteProp);
        const hasPtDestinations = showPartsTraderDestinations || requiresNote;

        let shouldPostMarketplaceNote = false;
        let shouldPostEstimatingNote = false;
        let marketplaceNoteToPost = notes;
        let estimatingNoteToPost = estimatingNoteProp;

        if (isEditable && noteDestination === 'both') {
            shouldPostMarketplaceNote = true;
            shouldPostEstimatingNote = true;
            marketplaceNoteToPost = drafts.both;
            estimatingNoteToPost = drafts.both;
        } else if (hasPtDestinations) {
            shouldPostMarketplaceNote = marketplaceDirty;
            shouldPostEstimatingNote = estimatingDirty;
            marketplaceNoteToPost = drafts.marketplace;
            estimatingNoteToPost = drafts.estimating;
        } else {
            // Estimate-only: commit estimating draft if dirty; list-price may still need marketplace mutation.
            shouldPostEstimatingNote = estimatingDirty;
            estimatingNoteToPost = drafts.estimating;
            marketplaceNoteToPost = notes;
        }

        const willPostMarketplace = shouldPostMarketplaceNote || shouldMutateListPrice;
        const willPostEstimating = shouldPostEstimatingNote;
        if (!willPostMarketplace && !willPostEstimating) {
            return;
        }

        setIsPending(true);
        onPendingChange?.(true);

        const promises: Promise<void>[] = [];

        if (willPostMarketplace) {
            const mutationData: {
                selectedOptionId: string;
                note: string;
                removeListPriceAdjustment: boolean;
                modifiedListPrice?: number;
                orderId?: string;
            } = {
                selectedOptionId,
                // Preserve existing PartsTrader note when only adjusting list price on Estimate-only.
                note: shouldPostMarketplaceNote ? marketplaceNoteToPost : notes,
                removeListPriceAdjustment: cannotAdjustListPrice
                    ? false
                    : shouldPostMarketplaceNote
                        ? removeAdjustment
                        : wantsRemoveListPrice,
                orderId,
            };

            if (wantsChangeListPrice) {
                mutationData.modifiedListPrice = parseFloat(modifiedListPrice);
            }

            promises.push(addNoteMutation.mutateAsync(mutationData));
        }

        if (willPostEstimating) {
            promises.push(addEstimatingNoteMutation.mutateAsync({
                orderId,
                orderPartId,
                note: estimatingNoteToPost,
            }));
        }

        try {
            await Promise.all(promises);
            await Promise.resolve(refetch()).catch(() => { });
            toast({
                title: "Note saved successfully",
                status: "success",
                duration: 3000,
                isClosable: true,
                position: 'bottom-right'
            });
            onSaved?.(shouldPostMarketplaceNote ? marketplaceNoteToPost : notes);
            setRemoveAdjustment(!cannotAdjustListPrice);
            setChangeAdjustment(false);
            setModifiedListPrice('');
            onClose();
        } catch (error: any) {
            toast({
                title: error?.response?.data?.message || "Failed to save",
                description: "Please try again",
                status: "error",
                duration: 3000,
                isClosable: true,
                position: 'bottom-right'
            });
        } finally {
            setIsPending(false);
            onPendingChange?.(false);
        }
    };

    useImperativeHandle(ref, () => ({
        save: handleSave,
        isPending,
    }));

    const fontSize = compact ? 'sm' : undefined;
    const inputSize = compact ? 'sm' : undefined;

    return (
        <Box display="flex" flexDirection="column" h="100%">
            <VStack spacing={4} align="stretch" flex={hideButtons ? undefined : "1"} minH={hideButtons ? undefined : 0} overflowY={hideButtons ? undefined : "auto"} px={compact ? 0 : hideButtons ? 0 : 6} py={compact ? 0 : hideButtons ? 0 : 4}>
                {canAdjustListPrice && cannotAdjustListPrice && (
                    <Alert
                        status="warning"
                        variant="subtle"
                        bg="orange.50"
                        border="1px solid"
                        borderColor="orange.200"
                        borderRadius="lg"
                        p={4}
                        alignItems="center"
                        overflow="visible"
                    >
                        <Icon
                            as={TbAlertTriangle as React.ElementType}
                            boxSize={5}
                            color="orange.400"
                            flexShrink={0}
                            mr={3}
                        />
                        <AlertDescription
                            fontSize="sm"
                            color="orange.800"
                            whiteSpace="normal"
                            lineHeight="1.4"
                        >
                            {LKQ_NO_ADJUST_CALLOUT}
                        </AlertDescription>
                    </Alert>
                )}
                {canAdjustListPrice && (
                    <Box
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="lg"
                        p={4}
                    >
                        <Text fontWeight="bold" fontSize={compact ? 'sm' : 'md'} color="gray.800" mb={3}>
                            Adjust List Price
                        </Text>
                        <VStack spacing={3} align="stretch">
                            {listPriceAdjusted && (
                                <Checkbox
                                    isChecked={removeAdjustment}
                                    isDisabled={cannotAdjustListPrice}
                                    onChange={(e) => {
                                        setRemoveAdjustment(e.target.checked);
                                        if (e.target.checked) {
                                            setChangeAdjustment(false);
                                            setModifiedListPrice('');
                                        }
                                    }}
                                    colorScheme="blue"
                                    size={compact ? 'sm' : undefined}
                                >
                                    <Text fontSize={fontSize}>Remove list price adjustment</Text>
                                </Checkbox>
                            )}
                            <Checkbox
                                isChecked={changeAdjustment}
                                isDisabled={cannotAdjustListPrice}
                                onChange={(e) => {
                                    setChangeAdjustment(e.target.checked);
                                    if (e.target.checked) {
                                        setRemoveAdjustment(false);
                                    }
                                    if (!e.target.checked) {
                                        setModifiedListPrice('');
                                    }
                                }}
                                colorScheme="blue"
                                size={compact ? 'sm' : undefined}
                            >
                                <Text fontSize={fontSize}>Change adjusted list price</Text>
                            </Checkbox>

                            {changeAdjustment && (
                                <FormControl pl={6}>
                                    <FormLabel fontSize="sm" color="gray.600">New Adjusted List Price</FormLabel>
                                    <InputGroup size={inputSize}>
                                        <InputLeftElement pointerEvents="none">
                                            <Text fontSize={fontSize || "md"} fontWeight="medium" color="gray.600">$</Text>
                                        </InputLeftElement>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            value={modifiedListPrice}
                                            onChange={(e) => setModifiedListPrice(e.target.value)}
                                            placeholder="Enter new price"
                                            pl="2.5rem"
                                            fontSize={fontSize}
                                            isDisabled={cannotAdjustListPrice}
                                        />
                                    </InputGroup>
                                </FormControl>
                            )}
                        </VStack>
                    </Box>
                )}

                {(isEditable || activeDraft.trim().length > 0) && (
                <Box
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="lg"
                    p={4}
                    py={5}
                >
                    {showHeader && (
                        <Text fontWeight="bold" fontSize={compact ? 'sm' : 'md'} color="gray.800" mb={3}>
                            Insurer Note
                        </Text>
                    )}

                    {isEditable && (
                        <Box mb={4}>
                            <FormLabel fontWeight="medium" mb={2} fontSize={fontSize} color="gray.500">Quick Select</FormLabel>
                            <Wrap spacing={2}>
                                {PREDEFINED_NOTES.map((predefinedNote, index) => (
                                    <WrapItem key={index}>
                                        <Tag
                                            size="md"
                                            variant="subtle"
                                            colorScheme="blue"
                                            textTransform="none"
                                            fontWeight="medium"
                                            letterSpacing="normal"
                                            cursor="pointer"
                                            _hover={{ bg: "blue.100", color: "blue.800" }}
                                            onClick={() => handlePredefinedNoteClick(predefinedNote)}
                                            transition="all 0.2s"
                                        >
                                            {predefinedNote}
                                        </Tag>
                                    </WrapItem>
                                ))}
                            </Wrap>
                        </Box>
                    )}

                    {(isEditable || activeDraft.length > 0) ? (
                        <Box>
                            <Flex align="center" justify="space-between" mb={3}>
                                <FormLabel fontWeight="medium" fontSize={fontSize} color="gray.500" mb={0}>Note</FormLabel>
                                {isEditable && (
                                    <SegmentedControl<NoteDestination>
                                        size="sm"
                                        value={noteDestination}
                                        onChange={handleDestinationChange}
                                        items={destinationOptions.map(({ key, label, disabled, disabledTooltip }) => ({
                                            value: key,
                                            label,
                                            isDisabled: disabled,
                                            tooltip: disabledTooltip,
                                        }))}
                                    />
                                )}
                            </Flex>
                            <Textarea
                                value={activeDraft}
                                onChange={(e) => isEditable && setActiveDraft(e.target.value)}
                                placeholder={isEditable ? "Enter note here..." : "No note available"}
                                rows={compact ? 3 : 4}
                                resize={isEditable ? "vertical" : "none"}
                                isReadOnly={!isEditable}
                                bg={!isEditable ? "gray.50" : "white"}
                                maxLength={499}
                                fontSize={fontSize}
                            />
                            {isEditable && (
                                <Text fontSize="xs" color="gray.500" mt={1} textAlign="right">
                                    {activeDraft.length} / 500 characters
                                </Text>
                            )}
                        </Box>
                    ) : null}
                </Box>
                )}
            </VStack>

            {!hideButtons && (
                <HStack
                    spacing={3}
                    justify="flex-end"
                    pt={4}
                    flexShrink={0}
                >
                    {!isPending && (
                        <Button
                            variant="outlineGray"
                            onClick={onClose}
                            size={compact ? 'sm' : 'md'}
                        >
                            {isEditable || canAdjustListPrice ? 'Cancel' : 'Close'}
                        </Button>
                    )}
                    {(isEditable || canAdjustListPrice) && (
                        <Button
                            onClick={handleSave}
                            isLoading={isPending}
                            loadingText="Applying..."
                            isDisabled={isPending || !selectedOptionId}
                            size={compact ? 'sm' : 'md'}
                            px={6}
                        >
                            Apply
                        </Button>
                    )}
                </HStack>
            )}
        </Box>
    );
});

export default PartOptionNoteForm;
